import { keys } from '@components/utils';

import type { DirectiveBinding } from 'vue';

export interface ITouchHandlers {
  start?: (wrapperEvent: { originalEvent: TouchEvent } & ITouchData) => void;
  end?: (wrapperEvent: { originalEvent: TouchEvent } & ITouchData) => void;
  move?: (wrapperEvent: { originalEvent: TouchEvent } & ITouchData) => void;
  left?: (wrapper: ITouchData) => void;
  right?: (wrapper: ITouchData) => void;
  up?: (wrapper: ITouchData) => void;
  down?: (wrapper: ITouchData) => void;
}

export interface ITouchData {
  touchstartX: number;
  touchstartY: number;
  touchmoveX: number;
  touchmoveY: number;
  touchendX: number;
  touchendY: number;
  offsetX: number;
  offsetY: number;
}

export type TTouchWrapper = ITouchHandlers & ITouchData;

export interface ITouchValue extends ITouchHandlers {
  parent?: boolean;
  options?: AddEventListenerOptions;
}

export interface ITouchStoredHandlers {
  touchstart: (event: TouchEvent) => void;
  touchend: (event: TouchEvent) => void;
  touchmove: (event: TouchEvent) => void;
}

export interface ITouchDirectiveBinding extends Omit<DirectiveBinding, 'value'> {
  value?: ITouchValue;
}

const handleGesture = (wrapper: TTouchWrapper) => {
  const { touchstartX, touchendX, touchstartY, touchendY } = wrapper;
  const dirRatio = 0.5;
  const minDistance = 16;

  wrapper.offsetX = touchendX - touchstartX;
  wrapper.offsetY = touchendY - touchstartY;

  if (Math.abs(wrapper.offsetY) < dirRatio * Math.abs(wrapper.offsetX)) {
    wrapper.left && touchendX < touchstartX - minDistance && wrapper.left(wrapper);
    wrapper.right && touchendX > touchstartX + minDistance && wrapper.right(wrapper);
  }

  if (Math.abs(wrapper.offsetX) < dirRatio * Math.abs(wrapper.offsetY)) {
    wrapper.up && touchendY < touchstartY - minDistance && wrapper.up(wrapper);
    wrapper.down && touchendY > touchstartY + minDistance && wrapper.down(wrapper);
  }
};

function touchstart(event: TouchEvent, wrapper: TTouchWrapper) {
  const touch = event.changedTouches[0];
  wrapper.touchstartX = touch.clientX;
  wrapper.touchstartY = touch.clientY;
  wrapper.start?.({ originalEvent: event, ...wrapper });
}

function touchend(event: TouchEvent, wrapper: TTouchWrapper) {
  const touch = event.changedTouches[0];
  wrapper.touchendX = touch.clientX;
  wrapper.touchendY = touch.clientY;
  wrapper.end?.({ originalEvent: event, ...wrapper });

  handleGesture(wrapper);
}

function touchMove(event: TouchEvent, wrapper: TTouchWrapper) {
  const touch = event.changedTouches[0];
  wrapper.touchmoveX = touch.clientX;
  wrapper.touchmoveY = touch.clientY;
  wrapper.move?.({ originalEvent: event, ...wrapper });
}

function createHandlers(value: ITouchHandlers = {}): ITouchStoredHandlers {
  const wrapper = {
    touchstartX: 0,
    touchstartY: 0,
    touchendX: 0,
    touchendY: 0,
    touchmoveX: 0,
    touchmoveY: 0,
    offsetX: 0,
    offsetY: 0,
    left: value.left,
    right: value.right,
    up: value.up,
    down: value.down,
    start: value.start,
    move: value.move,
    end: value.end
  };

  return {
    touchstart: (event: TouchEvent) => touchstart(event, wrapper),
    touchend: (event: TouchEvent) => touchend(event, wrapper),
    touchmove: (event: TouchEvent) => touchMove(event, wrapper)
  };
}

function mounted(el: HTMLElement, binding: ITouchDirectiveBinding) {
  const value = binding.value;
  const target = value?.parent ? el.parentElement : el;
  const options = value?.options ?? { passive: true };
  const uid = binding.instance?.$.uid; // @todo use custom uid generator

  if (!target || !uid) return;

  const handlers = createHandlers(binding.value);

  target._touchHandlers = target._touchHandlers ?? Object.create(null);
  target._touchHandlers![uid] = handlers;

  keys(handlers).forEach((eventName) => {
    target.addEventListener(eventName, handlers[eventName], options);
  });
}

function unmounted(el: HTMLElement, binding: ITouchDirectiveBinding) {
  const target = binding.value?.parent ? el.parentElement : el;
  const uid = binding.instance?.$.uid; // @todo use custom uid generator

  if (!target?._touchHandlers || !uid) return;

  const handlers = target._touchHandlers[uid];

  keys(handlers).forEach((eventName) => {
    target.removeEventListener(eventName, handlers[eventName]);
  });

  delete target._touchHandlers[uid];
}

export const Touch = { mounted, unmounted };

export default Touch;
